import React from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { profileActions } from "@/store";
import { useDynamicContext } from "@dynamic-labs/sdk-react-core";
import {
  UserIcon,
  LogOutIcon,
  HelpCircleIcon,
  BookOpenIcon,
  RocketIcon,
} from "lucide-react";

import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui";
import { useTranslate } from "@/hooks";
import { cn } from "@/helpers/tailwind";
import { AvatarBadge } from "../main";

interface AvatarPopupProps {
  resolvedUsername?: string;
  onSignOut?: () => void;
  shouldShowUserInfo?: boolean;
}

export const AvatarPopup: React.FC<AvatarPopupProps> = ({
  resolvedUsername,
  onSignOut,
  shouldShowUserInfo,
}) => {
  const t = useTranslate();
  const navigate = useNavigate();
  const { user } = useDynamicContext();
  const [isOpen, setIsOpen] = React.useState(false);
  const dispatch = useDispatch();

  const ActionButton = ({
    children,
    className,
    ...props
  }: React.ButtonHTMLAttributes<HTMLButtonElement>) => (
    <button
      className={cn(
        "flex items-center gap-2 rounded-md p-2",
        "text-sm text-slate-300 hover:bg-slate-800 [&>svg]:size-4",
        className
      )}
      {...props}>
      {children}
    </button>
  );

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger className="relative flex flex-row gap-4">
        <div className="cursor-pointer transition duration-150 hover:scale-95">
          <AvatarBadge className="size-10" ringClassName="border-slate-500" />
        </div>
      </PopoverTrigger>
      {shouldShowUserInfo && (
        <div className="flex w-full flex-col items-center lg:items-start">
          <button
            className={cn(
              "mb-1 text-center text-xs lg:text-start lg:text-base xl:text-lg"
            )}
            onClick={() => setIsOpen(!isOpen)}>
            {resolvedUsername}
          </button>
          <Button
            variant="ghost"
            className="flex h-auto items-center gap-2 px-0 py-0 text-xs font-normal text-slate-400 hover:bg-transparent hover:text-slate-400 focus:text-slate-400 lg:text-sm"
            onClick={onSignOut}>
            {t("auth.sign_out")}
            <LogOutIcon className="hidden !size-3.5 lg:flex lg:size-4" />
          </Button>
        </div>
      )}
      <PopoverContent className="absolute bottom-0 left-12 w-64 rounded-xl bg-slate-700 shadow-xl">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <AvatarBadge
              className="size-10 flex-shrink-0"
              ringClassName="border-slate-500"
            />
            <div className="flex flex-col overflow-hidden">
              <p className="truncate text-lg font-normal">
                {resolvedUsername ?? ""}
              </p>
              {user?.email && (
                <p className="mt-0.5 truncate text-sm text-slate-400">
                  {user?.email}
                </p>
              )}
            </div>
          </div>

          <div className="h-px w-full bg-slate-500" />
          <ActionButton
            onClick={() => {
              setIsOpen(false);
              navigate("/profile");
            }}>
            <UserIcon />
            <span>View profile</span>
          </ActionButton>
          <div className="flex flex-col gap-0.5">
            <ActionButton
              onClick={() => {
                setIsOpen(false);
                navigate("/faqs");
              }}>
              <HelpCircleIcon />
              <span>FAQ</span>
            </ActionButton>
            <ActionButton
              onClick={() => {
                setIsOpen(false);
                navigate("/terms-of-service");
              }}>
              <BookOpenIcon />
              <span>Terms of Service</span>
            </ActionButton>
            <ActionButton
              onClick={() => {
                setIsOpen(false);
                dispatch(
                  profileActions.setShowOnboarding({ fromQuickTour: true })
                );
              }}>
              <RocketIcon />
              <span>Quick Tour</span>
            </ActionButton>
          </div>
          <ActionButton
            onClick={() => {
              setIsOpen(false);
              onSignOut?.();
            }}>
            <LogOutIcon />
            <span>Sign out</span>
          </ActionButton>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default AvatarPopup;
